import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

const OFFER_ROLES = ['qualifying_product'];
const CART_COUNTDOWN_STORAGE_KEY = 'fnk-cart-countdown-deadline';
const CART_COUNTDOWN_DURATION_MS = 5 * 60 * 1000;

function readCartCountdownDeadline() {
  try {
    const deadline = Number(sessionStorage.getItem(CART_COUNTDOWN_STORAGE_KEY));
    return Number.isFinite(deadline) && deadline > 0 ? deadline : null;
  } catch {
    return null;
  }
}

function getOrCreateCartCountdownDeadline() {
  const storedDeadline = readCartCountdownDeadline();
  if (storedDeadline !== null) return storedDeadline;

  const deadline = Date.now() + CART_COUNTDOWN_DURATION_MS;

  try {
    sessionStorage.setItem(CART_COUNTDOWN_STORAGE_KEY, String(deadline));
  } catch {}

  return deadline;
}

function clearCartCountdownDeadline() {
  try {
    sessionStorage.removeItem(CART_COUNTDOWN_STORAGE_KEY);
  } catch {}
}

class CartCountdown extends HTMLElement {
  #intervalId = null;

  #deadline = null;

  connectedCallback() {
    this.#deadline = getOrCreateCartCountdownDeadline();
    this.#ensureTimeElement();
    this.#render();
  }

  disconnectedCallback() {
    this.#stop();
  }

  #render = () => {
    if (this.#deadline === null) return;

    const secondsRemaining = Math.max(0, Math.ceil((this.#deadline - Date.now()) / 1000));
    const time = this.querySelector('[data-cart-countdown-time]');
    const message = this.querySelector('[data-cart-countdown-message]');

    if (secondsRemaining === 0) {
      if (message instanceof HTMLElement && this.dataset.expiredMessage) {
        message.textContent = this.dataset.expiredMessage;
      }

      this.#stop();
      return;
    }

    if (time instanceof HTMLTimeElement) {
      const minutes = Math.floor(secondsRemaining / 60);
      const seconds = String(secondsRemaining % 60).padStart(2, '0');
      time.textContent = `${minutes}:${seconds}`;
      time.dateTime = `PT${secondsRemaining}S`;
    }

    if (this.#intervalId === null) {
      this.#intervalId = window.setInterval(this.#render, 1000);
    }
  };

  #ensureTimeElement() {
    const message = this.querySelector('[data-cart-countdown-message]');
    const activeMessage = this.dataset.activeMessage;
    if (!(message instanceof HTMLElement) || !activeMessage) return;

    const [prefix, suffix] = activeMessage.split('[[time]]');
    if (suffix === undefined) return;

    const time = document.createElement('time');
    time.dataset.cartCountdownTime = '';
    time.dateTime = 'PT5M';
    time.textContent = '5:00';
    message.replaceChildren(prefix, time, suffix);
  }

  #stop() {
    if (this.#intervalId === null) return;

    window.clearInterval(this.#intervalId);
    this.#intervalId = null;
  }
}

if (!customElements.get('cart-countdown')) {
  customElements.define('cart-countdown', CartCountdown);
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('change', this.#handleUpsellVariantChange);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    if (this.dataset.cartEmpty === 'true') {
      clearCartCountdownDeadline();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('change', this.#handleUpsellVariantChange);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /** @param {Event} event */
  #handleUpsellVariantChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-cart-upsell-variant-select]')) return;

    const form = select.closest('form');
    const addButton = form?.querySelector('[ref="addToCartButton"]');
    if (!(addButton instanceof HTMLButtonElement)) return;

    addButton.disabled = !select.value;
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    const sourceForm = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    const sourceRole = sourceForm?.dataset.bogoRole || '';
    const upsellPopup = document.querySelector('fnk-cart-upsell-popup[data-enabled="true"]');
    const addedFromUpsell = Boolean(sourceForm?.closest('fnk-cart-upsell-popup'));
    const shouldDeferToUpsell =
      upsellPopup &&
      customElements.get('fnk-cart-upsell-popup') &&
      !addedFromUpsell &&
      !sourceForm?.closest('cart-drawer-component') &&
      !event.detail?.data?.didError &&
      OFFER_ROLES.includes(sourceRole);

    if (shouldDeferToUpsell && typeof upsellPopup.openFor === 'function') {
      void upsellPopup.openFor(sourceRole, event.detail?.data?.productId || sourceForm?.dataset.productId);
    } else if (addedFromUpsell && typeof upsellPopup?.close === 'function') {
      upsellPopup.close();
    }

    if (this.hasAttribute('auto-open') && !shouldDeferToUpsell) {
      this.showDialog();
    }

    if (!event.detail?.data?.didError) {
      const cartItemCount = event.detail.resource?.item_count;
      const updatedItemCount =
        typeof cartItemCount === 'number'
          ? cartItemCount
          : event.detail?.data?.source === 'cart-items-component'
            ? event.detail.data.itemCount
            : null;

      if (updatedItemCount === 0) {
        clearCartCountdownDeadline();
      } else if (typeof updatedItemCount === 'number' && updatedItemCount > 0) {
        getOrCreateCartCountdownDeadline();
      }
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
